<div class="sav-site-container">
  <div class="sav-ch-page">
    <app-breadcrumb [items]="crumbs" />

    <!-- Hero -->
    <section class="sav-ch-hero" aria-label="Overview">
      @if (comparisonCount() > 0) {
        <span class="sav-ch-status">
          <span class="sav-ch-dot" aria-hidden="true"></span>
          {{ comparisonCount() }} comparison{{ comparisonCount() === 1 ? '' : 's' }} ·
          {{ storeCount() }} stores
          @if (lastUpdated()) {
            · updated {{ lastUpdated() }}
          }
        </span>
      }

      <h1>Store vs store: <span class="sav-ch-accent">which one saves you more?</span></h1>
      <p class="sav-ch-sub">
        Side-by-side coupon counts, best discounts, shipping and cashback — built from live store
        data, not opinion.
      </p>

      <div class="sav-ch-search">
        <input
          type="search"
          class="sav-ch-search-input"
          placeholder="Search a store or category"
          aria-label="Search comparisons"
          [value]="search()"
          (input)="onSearch($any($event.target).value)"
        />
        <span class="sav-ch-search-hint" aria-hidden="true">{{ comparisonCount() }} total</span>
      </div>
    </section>

    <!-- Featured -->
    @if (featured(); as top) {
      <a
        class="sav-ch-featured"
        [routerLink]="top.path"
        [attr.aria-label]="'Compare ' + top.storeA.name + ' vs ' + top.storeB.name"
      >
        <span class="sav-ch-cluster">
          <span class="sav-ch-cluster-logo">
            @if (top.storeA.logoImagePath) {
              <img [src]="top.storeA.logoImagePath | assetUrl" [alt]="" loading="eager" />
            } @else {
              {{ initials(top.storeA.name) }}
            }
          </span>
          <span class="sav-ch-cluster-vs" aria-hidden="true">VS</span>
          <span class="sav-ch-cluster-logo">
            @if (top.storeB.logoImagePath) {
              <img [src]="top.storeB.logoImagePath | assetUrl" [alt]="" loading="eager" />
            } @else {
              {{ initials(top.storeB.name) }}
            }
          </span>
        </span>

        <span class="sav-ch-featured-text">
          <span class="sav-ch-eyebrow">Most compared</span>
          <span class="sav-ch-featured-title">{{ top.storeA.name }} vs {{ top.storeB.name }}</span>
          <span class="sav-ch-featured-teaser">
            @if (top.teaser) {
              {{ top.teaser }}
            } @else {
              {{ compact(top.storeA.activeCoupons) }} vs
              {{ compact(top.storeB.activeCoupons) }} live offers compared side by side.
            }
          </span>
        </span>

        <span class="sav-ch-featured-cta">Read comparison →</span>
      </a>
    }

    <!-- All comparisons -->
    <section id="list" class="sav-ch-section" aria-labelledby="ch-list-heading">
      <div class="sav-ch-section-head">
        <h2 id="ch-list-heading">All comparisons</h2>
        <p class="sav-ch-count" aria-live="polite">{{ resultLabel() }}</p>
      </div>

      @if (categories().length > 0) {
        <div class="sav-ch-chips" role="group" aria-label="Filter by category">
          <button
            type="button"
            class="sav-ch-chip"
            [class.is-active]="activeCategory() === ''"
            [attr.aria-pressed]="activeCategory() === ''"
            (click)="selectCategory('')"
          >
            All · {{ comparisonCount() }}
          </button>
          @for (category of categories(); track category.slug) {
            <button
              type="button"
              class="sav-ch-chip"
              [class.is-active]="activeCategory() === category.slug"
              [attr.aria-pressed]="activeCategory() === category.slug"
              (click)="selectCategory(category.slug)"
            >
              {{ category.name }} · {{ category.count }}
            </button>
          }
        </div>
      }

      @if (visible().length > 0) {
        <div class="sav-ch-grid">
          @for (row of visible(); track row.id) {
            <article class="sav-ch-card">
              <div class="sav-ch-card-body">
                <div class="sav-ch-card-top">
                  <span class="sav-ch-cluster is-small">
                    <span class="sav-ch-cluster-logo">
                      @if (row.storeA.logoImagePath) {
                        <img [src]="row.storeA.logoImagePath | assetUrl" [alt]="" loading="lazy" />
                      } @else {
                        {{ initials(row.storeA.name) }}
                      }
                    </span>
                    <span class="sav-ch-cluster-vs" aria-hidden="true">VS</span>
                    <span class="sav-ch-cluster-logo">
                      @if (row.storeB.logoImagePath) {
                        <img [src]="row.storeB.logoImagePath | assetUrl" [alt]="" loading="lazy" />
                      } @else {
                        {{ initials(row.storeB.name) }}
                      }
                    </span>
                  </span>

                  @if (row.category) {
                    <span class="sav-ch-tag">{{ row.category.name }}</span>
                  }
                </div>

                <h3 class="sav-ch-card-title">{{ row.storeA.name }} vs {{ row.storeB.name }}</h3>

                <p class="sav-ch-card-teaser">
                  @if (row.teaser) {
                    {{ row.teaser }}
                  } @else {
                    {{ compact(row.storeA.activeCoupons) }} vs
                    {{ compact(row.storeB.activeCoupons) }} live offers compared side by side.
                  }
                </p>

                @if (row.winnerName) {
                  <p class="sav-ch-winner">
                    <span class="sav-ch-check" aria-hidden="true">✓</span>
                    Winner: <strong>{{ row.winnerName }}</strong>
                  </p>
                }
              </div>

              <a
                class="sav-ch-card-link"
                [routerLink]="row.path"
                [attr.aria-label]="'Compare ' + row.storeA.name + ' vs ' + row.storeB.name"
              >
                Compare <span aria-hidden="true">→</span>
              </a>
            </article>
          }
        </div>
      } @else if (!isLoading()) {
        <div class="sav-ch-empty">
          No comparisons match that search. Try a different store, or clear the category filter.
        </div>
      }
    </section>

    <!-- Methodology -->
    <section class="sav-ch-section" aria-labelledby="ch-method-heading">
      <div class="sav-ch-section-head">
        <h2 id="ch-method-heading">How we compare each store</h2>
        <p class="sav-ch-help">
          Every figure in a comparison table is read from live store data at request time — no
          manual scoring.
        </p>
      </div>

      <div class="sav-ch-method">
        <article class="sav-ch-method-card">
          <p class="sav-ch-method-label">Coupons</p>
          <h3>Coupons &amp; verification</h3>
          <p>
            Active coupon count and how many an admin has tested and marked verified, plus when the
            store's offers were last refreshed.
          </p>
        </article>
        <article class="sav-ch-method-card">
          <p class="sav-ch-method-label">Savings</p>
          <h3>Savings depth</h3>
          <p>
            The best and typical discount currently live, parsed from the offers themselves rather
            than from a headline claim.
          </p>
        </article>
        <article class="sav-ch-method-card">
          <p class="sav-ch-method-label">Perks</p>
          <h3>Shipping &amp; extras</h3>
          <p>
            Free shipping, cashback and student offers, taken from the store record, alongside its
            shopper rating and popularity.
          </p>
        </article>
      </div>
    </section>

    <!-- SEO copy -->
    <section class="sav-ch-section sav-ch-about" aria-labelledby="ch-about-heading">
      <!-- The heading and its one-line summary sit in the shared head wrapper; the long-form
           paragraphs below stay as body copy rather than being mistaken for the subtitle. -->
      <div class="sav-ch-section-head">
        <h2 id="ch-about-heading">Why compare before you shop?</h2>
        <p>Two stores rarely discount the same way — here is what that means for your basket.</p>
      </div>
      <p>
        Two stores rarely discount the same way. One runs frequent sitewide codes, another leans on
        seasonal clearance or bundles; one absorbs shipping, another charges it unless you hit a
        threshold. Comparing the two side by side shows which pattern actually fits the basket you
        are about to buy.
      </p>
      <p>
        These pages read their numbers from live coupon data, so a comparison reflects what is
        available today rather than what was true when it was written. Where a figure is unknown it
        is shown as a dash instead of a zero — a missing number and a genuine zero are different
        claims, and only one of them is honest.
      </p>
    </section>
  </div>
</div>
